import type { BoardTheme } from '@draughts/shared';
import { GameVariant, VARIANT_SPECS } from '@draughts/shared';

export interface BoardColors {
  light: string;
  dark: string;
}

/**
 * Explicit board themes (everything except the per-variant 'auto' default).
 *
 * Tre gruppi, in quest'ordine: le VARIANTI, le palette di legno e materiale, le palette
 * di colore. Le prime non sono un gusto ma i due colori con cui quella variante si gioca,
 * presi dalla MasterMatrix e non ricopiati qui — l'Internazionale non ha una voce sua
 * perché È `wood-classic`, stessi identici esadecimali (vedi il tipo `BoardTheme`).
 *
 * Le altre coprono famiglie diverse e non varianti della stessa idea: due legni, un verde
 * da torneo, un grigio-blu, un marmo, un alto contrasto, e i quattro colori tornati dal
 * ritiro (mogano, oceano, viola/lavanda, rosa). Erano stati tolti quando l'elenco è sceso a
 * sei, con l'argomento che un selettore libero rende inutile una lista lunga: vale per le
 * tinte vicine — 'sand' e 'slate' restano fuori, erano il legno e il blu/grigio un'altra
 * volta — non per le famiglie, e sei tinte imparentate si leggono tutte uguali.
 *
 * SUL VINCOLO che qui era documentato. Diceva: nessun `dark` sotto la luminanza di
 * `#4E8FA8`, altrimenti il pallino verde degli aiuti sparisce. La soglia era sbagliata
 * (`ocean` sta a metà classifica, non al limite) e soprattutto il vincolo non è mai stato
 * rispettato. Contrasto WCAG del pallino sulla propria casella scura, dal peggio, sulle
 * sei palette rimaste:
 *
 *   walnut 1.041 · high-contrast 1.111 · … · marble/green-cream/blue-grey ~1.2
 *
 * MA QUEI NUMERI SONO WCAG, cioè la metrica che questo progetto ha scartato proprio qui:
 * guarda la sola luminanza, quindi condanna un verde chiaro su un bruno chiaro anche
 * quando le due tinte sono lontanissime. La metrica di casa è la distanza Oklab di
 * `board-highlight-contrast.test.ts`, e in quella nessun tema è mai stato al limite. Il
 * verdetto "già illeggibile" era un artefatto della metrica sbagliata, non un difetto aperto.
 *
 * ORA ESISTE, invece, il modulo che questo commento dava per assente: i due segni di
 * destinazione — il pallino della mossa e l'anello della presa — prendono il colore da
 * `board-highlight-colors.ts`, che lo DERIVA dalla casella invece di tenerlo costante.
 * Quindi un `dark` nuovo non ha vincoli da rispettare: se avvicina il verde, è il verde a
 * schiarirsi. Restano costanti in GameBoard.css le altre `--hl-*` (velature dell'ultima
 * mossa, della presa, del premove, dell'aiuto), coperte come prima dalla scelta dei colori
 * e verificate dal test sui soli fondi SPEDITI; per quelle il tema 'custom' non è coperto —
 * vedi ColorPickerOverlay.tsx per dove andrebbe messo l'avviso.
 */
export const BOARD_THEME_COLORS: Record<Exclude<BoardTheme, 'auto' | 'custom'>, BoardColors> = {
  // Current default — warm wooden palette via project tokens. È anche, alla lettera, la
  // scacchiera dell'Internazionale: `#F0D9B5/#B58863` in index.css e in variants.jsonc.
  'wood-classic': {
    light: 'var(--color-beige-warm)',
    dark: 'var(--color-brown-medium)',
  },
  // Le tre varianti si DERIVANO dalla MasterMatrix invece di ricopiarne gli esadecimali:
  // così "Italiana" resta l'italiana anche dopo un ritocco di variants.jsonc, che è la sola
  // cosa che il nome promette. `variantBoardColors` è una dichiarazione di funzione, quindi
  // è hoistata e può stare qui sotto.
  italian: variantBoardColors(GameVariant.ITALIAN),
  english: variantBoardColors(GameVariant.ENGLISH),
  brazilian: variantBoardColors(GameVariant.BRAZILIAN),
  walnut: {
    light: '#E8C99B',
    dark: '#7B4A24',
  },
  'green-cream': {
    light: '#EEEED2',
    dark: '#769656',
  },
  'blue-grey': {
    light: '#DEE3E6',
    dark: '#788A94',
  },
  marble: {
    light: '#F0F0F0',
    dark: '#A0A0A0',
  },
  'high-contrast': {
    light: '#FFFFFF',
    dark: '#6E6E6E',
  },
  // Tornate dal ritiro, con gli stessi esadecimali di allora: chi le aveva scelte prima del
  // taglio se le ritrova per nome invece che come "Personalizzato…".
  mahogany: {
    light: '#EBD3B0',
    dark: '#6B3226',
  },
  ocean: {
    light: '#D6ECF5',
    dark: '#4E8FA8',
  },
  'purple-lavender': {
    light: '#E8DFF5',
    dark: '#7E64A8',
  },
  rose: {
    light: '#F7E1E6',
    dark: '#B5748A',
  },
};

/** The square colors defined for a variant in the MasterMatrix. */
export function variantBoardColors(variant: GameVariant): BoardColors {
  const c = VARIANT_SPECS[variant].ui.colors;
  return { light: c.lightSquare, dark: c.darkSquare };
}

/**
 * Resolve the square colors for a board.
 * With the 'auto' theme (default) the colors come from the active variant's
 * MasterMatrix entry, so each variant shows its own traditional shades.
 * Any explicit theme overrides the per-variant default.
 */
export function resolveBoardColors(
  theme: BoardTheme,
  variant: GameVariant,
  custom?: BoardColors,
): BoardColors {
  if (theme === 'auto') return variantBoardColors(variant);
  // Con 'custom' i due esadecimali dell'utente SONO i colori di casella, senza
  // rielaborazioni e senza limiti: il selettore lascia scegliere liberamente, chiarezza
  // e saturazione comprese. Quella libertà non è coperta da nessun meccanismo: le
  // evidenziazioni hanno colori fissi e — da quando i contorni scuri sono stati tolti —
  // un solo strato, quindi un fondo scelto vicino a una di esse la attenua. È un
  // compromesso accettato; vedi ColorPickerOverlay.tsx.
  if (theme === 'custom') return custom ?? variantBoardColors(variant);
  return BOARD_THEME_COLORS[theme] ?? BOARD_THEME_COLORS['wood-classic'];
}

/**
 * Il colore di una casella come ESADECIMALE, per chi ci deve fare i conti.
 *
 * `BOARD_THEME_COLORS` per `wood-classic` non porta due esadecimali ma due token CSS, e sta
 * bene così: il legno classico è il default dell'app e i suoi due colori vivono in index.css
 * insieme al resto della palette. Chi però il colore lo deve MISURARE — `board-highlight-colors.ts`,
 * che ne ricava l'inchiostro dei segni di destinazione — gira in JavaScript, dove `var(...)` è
 * una stringa e basta.
 *
 * Gli altri casi l'esadecimale ce l'hanno già: i colori per-variante escono dalla MasterMatrix e
 * quelli 'custom' dal selettore, che li produce con `oklchToHex`. Quindi la mappa qui sotto ha
 * due voci e non è destinata a crescere; che i suoi valori siano ancora quelli di index.css lo
 * verifica `board-highlight-contrast.test.ts`, che il CSS lo rilegge davvero.
 */
const BOARD_TOKEN_HEX: Record<string, string> = {
  '--color-beige-warm': '#F0D9B5',
  '--color-brown-medium': '#B58863',
};

export function boardColorHex(value: string): string {
  const token = value.trim().match(/^var\(\s*(--[\w-]+)/);
  if (!token) return value.trim();
  return BOARD_TOKEN_HEX[token[1]!] ?? value.trim();
}
